import { useState, useRef, useEffect, useMemo } from 'react';
import {
  mdiCheck,
  mdiCloseCircle,
  mdiContentCopy,
  mdiGamepadVariantOutline,
  mdiSync,
} from '@mdi/js';
import { TFunction, useTranslation } from '../../i18n';

enum CopyState {
  Idle,
  Copying,
  Copied,
  Error,
}

type ButtonColor = 'primary' | 'secondary' | 'success' | 'error' | 'info';

const getInfo = (
  copyState: CopyState,
  copyClicksCount: number,
  t: TFunction
): [string, ButtonColor, string] => {
  switch (copyState) {
    case CopyState.Idle:
      return [t('Copy'), 'primary', mdiContentCopy]; // Do not define color because of grayed for disabled...
    case CopyState.Copying:
      return [t('Copying'), 'primary', mdiSync];
    case CopyState.Error:
      return [t('Error'), 'error', mdiCloseCircle];
    case CopyState.Copied:
      switch (Math.floor(copyClicksCount / 6)) {
        case 0:
          return [t('Copied'), 'success', mdiCheck];
        case 1:
          return [t('Copied for sure'), 'success', mdiCheck];
        case 2:
          return [t('100% copied'), 'success', mdiCheck];
        case 3:
        case 4:
        case 5:
          return [t('146% copied'), 'error', mdiCheck];
        default:
          return [t('Game over.'), 'info', mdiGamepadVariantOutline];
      }
    default:
      throw new Error(`Unknown copy state ${copyState}`);
  }
};

export const useCopy = (
  text: string
): {
  copy: () => void;
  copyState: CopyState;
  title: string;
  colorIcon: ButtonColor;
  path: string;
} => {
  const { t } = useTranslation();

  const [copyState, setCopyState] = useState<CopyState>(CopyState.Idle);
  const [copyCount, setCopyCount] = useState<number>(0);

  const mounted = useRef<boolean>(true);
  const timeout = useRef<any>();

  const readyData = useMemo(() => {
    const [title, colorIcon, path] = getInfo(copyState, copyCount, t);
    return { title, colorIcon, path };
  }, [copyState, copyCount]);

  const restartTimer = () => {
    if (!mounted.current) {
      return;
    }
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setCopyState(CopyState.Idle);
      setCopyCount(0);
    }, 1500);
  };

  const copy = () => {
    setCopyCount((c) => c + 1);
    if (copyState === CopyState.Copied) {
      restartTimer();
      return;
    } else if (copyState !== CopyState.Idle) {
      return;
    }

    setCopyState(CopyState.Copying);
    navigator.clipboard
      .writeText(text)
      .then(() => mounted.current && setCopyState(CopyState.Copied))
      .catch(() => mounted.current && setCopyState(CopyState.Error))
      .finally(restartTimer);
  };

  useEffect(
    () => () => {
      clearTimeout(timeout.current);
      mounted.current = false;
    },
    []
  );

  return { copy, copyState, ...readyData };
};
